import * as React from 'react';
import InputHandler from 'Components/InputHandler';
import colors from '../../../../../config/theme/colors';
import { formatSSNInvalidityReason, validateSSN } from 'neuro-utils/ssn';
import ActionButtonRounded from 'Components/ActionButtonRounded';
import type { IMyServiceContext } from '../..';
import { fetchMyUserContactInfo, fetchMyUserDataBySsn } from 'Store/myapp/fetchers';
import phone from 'phone';
import { validate as emailValidate } from '@stellarq/email';
import { isPatientCountryCode, PATIENTCOUNTRYCODES } from 'neuro-constants';

const formatSSNInvalidityText = ({
  ssn,
  ssnLocalization,
  locale,
}: {
  ssn: string;
  ssnLocalization: SSNCountryCode;
  locale: Locale;
}): string | undefined => {
  const validityResult = validateSSN(ssnLocalization, { ssn });
  if (validityResult.isValid) return undefined;
  return formatSSNInvalidityReason({ reason: validityResult.reason, locale });
};

const RelativeAddingRow = ({ title, children }: { title: string; children: React.JSX.Element }) => {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2rem' }}>
      <div style={{ color: colors.tertiaryText }}>{title}</div>
      {children}
    </div>
  );
};

const initialInformant: NewInformant = {
  ssnLocalization: 'FIN',
  ssn: undefined,
  lastName: undefined,
  firstName: undefined,
  phoneNumber: undefined,
  email: undefined,
};

export const NewInformant = ({
  fm,
  locale,
  onChangeTaskForm,
  setAddingNewInformant,
  editing,
}: IOwnProps): React.JSX.Element => {
  const [newInformant, setNewInformant] = React.useState(initialInformant);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [mysqUserDataFound, setMysqUserDataFound] = React.useState<boolean>(false);

  const [validFields, setValidFields] = React.useState<{ ssn: boolean; phone: boolean; email: boolean }>({
    ssn: true,
    phone: true,
    email: true,
  });

  const handleMysqUserDataFetching = async ({
    ssn,
    ssnLocalization,
  }: {
    ssn: string;
    ssnLocalization: PatientCountryCode;
  }) => {
    setLoading(true);
    const userData = await fetchMyUserDataBySsn({ ssn, ssnLocalization });
    if (!userData) {
      setLoading(false);
      return;
    }
    setMysqUserDataFound(true);
    const contacts = await fetchMyUserContactInfo(userData.userId);
    const email = contacts?.find(c => c.contactType === 'email')?.contactValue[0];
    const phone = contacts?.find(c => c.contactType === 'sms')?.contactValue[0];
    const firstName = userData.firstNames;
    const lastName = userData.lastNames;
    setNewInformant(prev => ({
      ssnLocalization: prev.ssnLocalization,
      ssn: prev.ssn,
      lastName,
      firstName,
      phoneNumber: phone,
      email,
    }));
    setLoading(false);
  };

  return (
    <>
      <RelativeAddingRow title={fm('general.country')}>
        <InputHandler
          name="ssnLocalization"
          type="Select"
          editing={!!editing}
          options={PATIENTCOUNTRYCODES} // this should come from org settings? TODO
          placeholder={fm('general.country')}
          formData={{
            document: { ssnLocalization: newInformant.ssnLocalization },
            onChange: values => {
              const value = Object.values(values)[0];
              if (!isPatientCountryCode(value)) return;
              setNewInformant(prev => ({ ...prev, ssnLocalization: value }));
            },
          }}
        />
      </RelativeAddingRow>
      <RelativeAddingRow title={fm('general.ssn')}>
        <InputHandler
          name="ssn"
          type="TextField"
          editing={!!editing}
          placeholder={'general.ssn'}
          formData={{
            document: { ssn: newInformant.ssn },
            onChange: async (values: TOnChangeValues) => {
              const value = Object.values(values)[0];
              if (typeof value === 'string') {
                const { isValid } = validateSSN(newInformant.ssnLocalization, { ssn: value });
                setValidFields(prev => ({ ...prev, ssn: isValid })); // unnecessary? TODO
                setNewInformant(prev => ({ ...prev, ssn: value }));
                if (isValid && value !== newInformant.ssn) {
                  await handleMysqUserDataFetching({ ssn: value, ssnLocalization: newInformant.ssnLocalization });
                } else if (!isValid && mysqUserDataFound) {
                  // what does this block do?? TODO
                  setMysqUserDataFound(false);
                  setNewInformant({
                    ...initialInformant,
                    ssn: value,
                  });
                }
              }
            },
          }}
          invalidValue={!validateSSN(newInformant.ssnLocalization, { ssn: newInformant.ssn ?? '' })}
          invalidText={formatSSNInvalidityText({
            ssn: newInformant.ssn ?? '',
            ssnLocalization: newInformant.ssnLocalization,
            locale,
          })}
        />
      </RelativeAddingRow>
      {/** should firstNames be asked first?? TODO */}
      <RelativeAddingRow title={fm('general.lastNames')}>
        <InputHandler
          name="lastName"
          type="TextField"
          editing={!!editing}
          placeholder={'general.lastNames'}
          formData={{
            document: { lastName: newInformant.lastName },
            onChange: (values: TOnChangeValues) => {
              const value = Object.values(values)[0];
              if (typeof value === 'string') {
                setNewInformant(prev => ({ ...prev, lastName: value }));
              }
            },
          }}
          disabled={mysqUserDataFound}
        />
      </RelativeAddingRow>
      <RelativeAddingRow title={fm('general.firstNames')}>
        <InputHandler
          name="firstName"
          type="TextField"
          editing={!!editing}
          placeholder={'general.firstNames'}
          formData={{
            document: { firstName: newInformant.firstName },
            onChange: (values: TOnChangeValues) => {
              const value = Object.values(values)[0];
              if (typeof value === 'string') {
                setNewInformant(prev => ({ ...prev, firstName: value }));
              }
            },
          }}
          disabled={mysqUserDataFound}
        />
      </RelativeAddingRow>
      <RelativeAddingRow title={fm('profile.phoneNumber')}>
        <InputHandler
          name="phoneNumber"
          type="TextField"
          editing={!!editing}
          placeholder={'profile.phoneNumber'}
          formData={{
            document: { phoneNumber: newInformant.phoneNumber },
            onChange: (values: TOnChangeValues) => {
              const value = Object.values(values)[0];
              if (typeof value === 'string') {
                // TODO implement separate phone number input with built-in country selection + number validation and normalization
                const parsedPhone = phone(value, { country: newInformant.ssnLocalization });
                setValidFields(prev => ({ ...prev, phone: value === '' ? true : parsedPhone.isValid }));
                setNewInformant(prev => ({ ...prev, phoneNumber: value }));
              }
            },
          }}
          invalidValue={!validFields.phone && (newInformant.phoneNumber?.length ?? 0) > 0}
          invalidText={fm('delegates.checkPhone')}
          disabled={mysqUserDataFound}
        />
      </RelativeAddingRow>
      <RelativeAddingRow title={fm('profile.email')}>
        <InputHandler
          name="email"
          type="TextField"
          editing={!!editing}
          placeholder={'profile.email'}
          formData={{
            document: { email: newInformant.email },
            onChange: (values: TOnChangeValues) => {
              const value = Object.values(values)[0];
              if (typeof value === 'string') {
                setValidFields(prev => ({ ...prev, email: value === '' ? true : emailValidate(value) }));
                setNewInformant(prev => ({ ...prev, email: value }));
              }
            },
          }}
          invalidValue={!validFields.email && (newInformant.email?.length ?? 0) > 0}
          invalidText={fm('delegates.checkEmail')}
          disabled={mysqUserDataFound}
        />
      </RelativeAddingRow>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '2rem' }}>
        <ActionButtonRounded
          text="general.cancel"
          onClick={() => {
            setAddingNewInformant(false);
            setNewInformant(initialInformant);
            setValidFields({ ssn: true, email: true, phone: true });
            setMysqUserDataFound(false);
          }}
          width={9.6}
          height={3}
          fontSize={16}
          uppercase={false}
          loading={loading}
        />
        <ActionButtonRounded
          text={'general.add'}
          onClick={() => {
            setAddingNewInformant(false);
            // TODO is ssn fine here? feels a bit suspicious
            onChangeTaskForm({ newInformant, informant_id: newInformant.ssn });
            setNewInformant(initialInformant);
            setValidFields({ ssn: true, phone: true, email: true });
            setMysqUserDataFound(false);
          }}
          width={9.6}
          height={3}
          fontSize={16}
          uppercase={false}
          filled
          loading={loading}
          disabled={
            !Object.values(validFields).every(val => val) ||
            !(newInformant.ssn && (newInformant.email || newInformant.phoneNumber))
          }
        />
      </div>
    </>
  );
};

type NewInformant = {
  ssnLocalization: PatientCountryCode;
  ssn?: string;
  lastName?: string;
  firstName?: string;
  phoneNumber?: string;
  email?: string;
};

interface IOwnProps extends Pick<IMyServiceContext<'_tasklist'>, 'fm'> {
  onChangeTaskForm: (tl: Partial<ITaskList>) => void;
  setAddingNewInformant: (val: boolean) => void;
  editing: boolean;
  locale: Locale;
}
